import { supabase } from "../supabaseClient";
import { FunctionsHttpError } from "@supabase/functions-js";

export interface AiCompletionParams {
  prompt: string;
  systemInstruction?: string;
  maxOutputTokens?: number;
  /** 0 = déterministe, 1 = créatif. Pour l'aide CRM, ~0.4 donne des réponses plus stables. */
  temperature?: number;
}

export interface AiCompletionResult {
  text: string;
}

/**
 * Appel générique à l'Edge Function ai-completion (Gemini).
 * Utilisé par les features IA du CRM (suggestions notes, LED, stock, recherche).
 */
export async function invokeAiCompletion(params: AiCompletionParams): Promise<AiCompletionResult> {
  const { data, error } = await supabase.functions.invoke("ai-completion", {
    body: {
      prompt: params.prompt,
      systemInstruction: params.systemInstruction ?? undefined,
      maxOutputTokens: params.maxOutputTokens ?? 1024,
      temperature: params.temperature,
    },
  });

  if (error) {
    if (error instanceof FunctionsHttpError && error.context && typeof (error.context as Response).json === "function") {
      try {
        const body = await (error.context as Response).json();
        if (body && typeof body.error === "string") throw new Error(body.error);
      } catch (e) {
        if (e instanceof Error && e.message !== error.message) throw e;
      }
    }
    throw new Error(error.message || "Erreur appel IA");
  }

  const err = (data as { error?: string })?.error;
  if (err) {
    throw new Error(err);
  }

  const text = (data as { text?: string })?.text;
  if (text == null) {
    throw new Error("Réponse IA invalide");
  }

  return { text };
}

export type AiSuggestJobType = "notes" | "led_estimate";

export interface AiSuggestJobParams {
  type: AiSuggestJobType;
  job: {
    clientAddress?: string;
    surface?: string;
    lightingType?: string;
    ceilingHeight?: string;
    notes?: string;
  };
}

/**
 * Suggestion de notes ou estimation LED pour un dossier (appelle ai-suggest-job).
 */
export async function invokeAiSuggestJob(params: AiSuggestJobParams): Promise<{ suggestion: string }> {
  const { data, error } = await supabase.functions.invoke("ai-suggest-job", {
    body: { type: params.type, job: params.job },
  });
  if (error) {
    if (error instanceof FunctionsHttpError && error.context && typeof (error.context as Response).json === "function") {
      try {
        const body = await (error.context as Response).json();
        if (body && typeof body.error === "string") throw new Error(body.error);
      } catch (e) {
        if (e instanceof Error && e.message !== error.message) throw e;
      }
    }
    throw new Error(error.message || "Erreur suggestion IA");
  }
  const err = (data as { error?: string })?.error;
  if (err) throw new Error(err);
  const suggestion = (data as { suggestion?: string })?.suggestion;
  if (suggestion == null) throw new Error("Réponse IA invalide");
  return { suggestion };
}

export interface AiSuggestStockPrepareParams {
  job: {
    surface?: string;
    lightingType?: string;
    ceilingHeight?: string;
    estimatedLedCount?: string;
  };
  inventoryNames: Array<{ id: string; name: string }>;
  recentJobStock?: Array<{
    surface?: string;
    lightingType?: string;
    stockUsed: Record<string, number>;
  }>;
}

/**
 * Suggestion de quantités à préparer par référence (appelle ai-suggest-stock-prepare).
 */
export async function invokeAiSuggestStockPrepare(
  params: AiSuggestStockPrepareParams
): Promise<{ suggestion: Record<string, number> }> {
  const { data, error } = await supabase.functions.invoke("ai-suggest-stock-prepare", {
    body: {
      job: params.job,
      inventoryNames: params.inventoryNames,
      recentJobStock: params.recentJobStock ?? [],
    },
  });
  if (error) {
    if (error instanceof FunctionsHttpError && error.context && typeof (error.context as Response).json === "function") {
      try {
        const body = await (error.context as Response).json();
        if (body && typeof body.error === "string") throw new Error(body.error);
      } catch (e) {
        if (e instanceof Error && e.message !== error.message) throw e;
      }
    }
    throw new Error(error.message || "Erreur suggestion stock");
  }
  const err = (data as { error?: string })?.error;
  if (err) throw new Error(err);
  const suggestion = (data as { suggestion?: Record<string, number> })?.suggestion;
  if (suggestion == null) throw new Error("Réponse IA invalide");
  return { suggestion };
}

export interface AiNaturalSearchParams {
  query: string;
  context: {
    partnerNames: string[];
    installerNames: string[];
    regionNames?: string[];
  };
}

export interface AiNaturalSearchResult {
  partnerName: string | null;
  installerName: string | null;
  status: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  regionName: string | null;
  freeText: string | null;
}

/**
 * Recherche en langage naturel : retourne des filtres (noms à mapper aux IDs côté front).
 */
export async function invokeAiNaturalSearch(
  params: AiNaturalSearchParams
): Promise<AiNaturalSearchResult> {
  const { data, error } = await supabase.functions.invoke("ai-natural-search", {
    body: {
      query: params.query,
      context: {
        partnerNames: params.context.partnerNames,
        installerNames: params.context.installerNames,
        regionNames: params.context.regionNames ?? [],
      },
    },
  });

  if (error) {
    if (error instanceof FunctionsHttpError && error.context && typeof (error.context as Response).json === "function") {
      try {
        const body = await (error.context as Response).json();
        if (body && typeof body.error === "string") throw new Error(body.error);
      } catch (e) {
        if (e instanceof Error && e.message !== error.message) throw e;
      }
    }
    throw new Error(error.message || "Erreur recherche IA");
  }
  const err = (data as { error?: string })?.error;
  if (err) throw new Error(err);
  return data as AiNaturalSearchResult;
}
